<template>
    <ion-page class="ion-page" id="main">
        <default-header :title="$t('ncrypt.header_title')"></default-header>

        <ion-content class="bg-image" fullscreen>
            <background slot="fixed"/>
            <ion-card class="floating floating-small" style="margin-bottom: 0;">
                <ion-item color="primary" style="text-align: center; font-size: 100%;">
                    <ion-text style="padding: 10px 0;">
                        {{ $t('ncrypt.verify_open_message.open_title') }}
                    </ion-text>
                </ion-item>
            </ion-card>
            <ion-card v-show="!loading && !error" class="floating floating-small" style="text-align: center; margin-top: 0;">
                <ion-card-header>
                    <ion-icon md="shield" ios="shield" style="font-size: 64px; margin: 15px 0 10px;" color="primary"></ion-icon>
                    <ion-card-title>{{ $t('ncrypt.verify_open_message.email_verification') }}</ion-card-title>
                </ion-card-header>
                <ion-card-content>
                    <div>
                        <div v-if="!secondEmailSent">
                            <p>{{ $t('ncrypt.verify_open_message.open_description') }}</p>
                            <br/>
                            <ion-button @click="sendSecondEmail()" style="margin: 0 auto;">{{ $t('ncrypt.verify_open_message.send_verification_email_button') }}</ion-button>
                            <br/>
                            <br/>
                            <p>{{ $t('ncrypt.verify_open_message.send_verification_email_instruction') }}</p>
                        </div>
                        <div v-else>
                            <i18n path="ncrypt.verify_open_message.verification_mail_sent"
                                  tag="p">
                                <template v-slot:email>
                                    <strong>{{ sentToEmail }}</strong>
                                </template>
                            </i18n>
                            <br/>
                            <p>{{ $t('ncrypt.verify_open_message.verification_mail_sent_instruction') }}</p>
                            <ion-item lines="none" style="margin: 25px 0;">
                                <ion-input inputmode="number"
                                           type="text"
                                           ref="updateAccessCode"
                                           @input="updateAccessCode"
                                           style="
                                        border: 1px solid #888;
                                        border-radius: 8px;
                                        padding: 0 10px !important;
                                        text-align: center;"
                                           :placeholder="$t('ncrypt.verify_open_message.input_access_code_placeholder')"/>
                            </ion-item>
                            <ion-item lines="none">
                                <ion-button @click="validateAccessCode()"
                                            expand="block"
                                            size="default"
                                            shape="round"
                                            style="margin: 0 auto;">
                                    {{ $t('ncrypt.verify_open_message.input_access_code_button') }}
                                </ion-button>
                            </ion-item>
                        </div>
                    </div>
                </ion-card-content>
            </ion-card>
            <ion-card class="floating floating-small" v-if="error">
                <ion-card-header>
                    <ion-card-title>{{ errorSubject }}</ion-card-title>
                </ion-card-header>
                <ion-card-content>
                    <p v-html="errorMessage"/>
                </ion-card-content>
            </ion-card>
        </ion-content>
    </ion-page>
</template>

<script>
    import {ApiError} from "../errors/ApiError";
    import {ErrorHandler} from "../services/ErrorHandler";
    import {Notifier} from "../services/Notifier";
    import DefaultHeader from "./nav/DefaultHeader.vue";
    import Background from "../views/Background";

    export default {
        components: {Background, DefaultHeader},
        data() {
            return {
                access_code: null,
                loading: true,
                error: false,
                errorSubject: this.$t('ncrypt.errors.alert_default_title'),
                errorMessage: null,
                sentToEmail: '',
                fromDisplayName: '',
                secondEmailSent: false,
                secondEmailSentBefore: false,
                open_id: null,
            }
        },
        methods: {
            sendSecondEmail() {
                return new Promise((resolve, reject) => {
                    this.$http
                        .post(`/m/${this.$route.params.access_token}/click/${this.open_id}`)
                        .then(async ({data}) => {
                            if (!data || !data.success) {
                                this.error = true;
                                this.errorMessage = ErrorHandler.buildApiErrorMessage(data);
                                new ApiError(this.errorMessage, false);
                                return;
                            }

                            this.secondEmailSent = true;
                            if (!data.sent_token) {
                                this.secondEmailSentBefore = true;
                                await this.emailSentBeforeToast();
                            }

                            resolve();
                        })
                        .catch(error => reject(error));
                });
            },
            openMessage() {
                return new Promise((resolve, reject) => {
                    this.$http
                        .post(`/m/${this.$route.params.access_token}/open`)
                        .then(async ({data}) => {
                            if (!data || !data.success) {
                                return reject(data, false);
                            }

                            if (data.authed_access) {
                                if (this.loading && this.loader) {
                                    await this.loader.dismiss();
                                }

                                await this.$store.dispatch('refreshUser');

                                await this.$router.push({
                                    name: 'message',
                                    params: {id: data.message_id},
                                });
                                return;
                            }

                            if (data.wrong_user) {
                                await this.$store.dispatch('refreshUser');
                            }

                            if (data.already_has_access) {
                                if (this.loading && this.loader) {
                                    await this.loader.dismiss();
                                }
                                await this.$router.push({
                                    name: 'open-verify', params: {
                                        access_token: this.$route.params.access_token,
                                        email_token: data.email_token,
                                    },
                                });
                                return;
                            }

                            this.open_id = data.open_id;

                            this.sentToEmail = data.sent_to_email;
                            this.fromDisplayName = data.from_display_name;

                            if (data.open_is_clicked) {
                                this.secondEmailSent = true;
                                this.secondEmailSentBefore = true;

                                await this.emailSentBeforeToast();
                            }

                            resolve();
                        })
                        .catch(error => reject(error, true));
                });
            },
            async emailSentBeforeToast() {
                await Notifier.showToast({
                    message: this.$t('ncrypt.errors.open_message_verification_mail_already_sent'),
                    color: 'primary',
                    showCloseButton: true,
                });
            },
            updateAccessCode() {
                this.access_code = this.$refs.updateAccessCode.value;
            },
            validateAccessCode() {
                this.$http
                    .post(`/m/${this.$route.params.access_token}/validate-access-code`, {
                        access_code: this.access_code,
                    })
                    .then(async ({data}) => {
                        if (!data || !data.success) {
                            this.errorMessage = ErrorHandler.buildApiErrorMessage(data);
                            new ApiError(this.errorMessage);
                            return;
                        }

                        if (data.authed_access) {
                            if (this.loading && this.loader) {
                                await this.loader.dismiss();
                            }

                            await this.$store.dispatch('refreshUser');

                            await this.$router.push({
                                name: 'message',
                                params: {id: data.message_id},
                            });
                            return;
                        }

                        if (!data.email_token) {
                            this.errorMessage = ErrorHandler.buildApiErrorMessage(data);
                            new ApiError(this.errorMessage);
                            return;
                        }

                        await this.$router.push({
                            name: 'open-verify',
                            params: {
                                access_token: this.$route.params.access_token,
                                email_token: data.email_token,
                            },
                        });
                    })
                    .catch(error => {
                        this.errorMessage = ErrorHandler.buildApiErrorMessage(error);
                        new ApiError(this.errorMessage, true);
                    });
            },
        },
        async created() {
            this.loader = await this.$ionic.loadingController.create({duration: 10000});

            await this.loader.present();

            this.openMessage()
                .catch((error, shouldLogError) => {
                    this.error = true;
                    if (error.subject) {
                        this.errorSubject = error.subject;
                    }
                    this.errorMessage = ErrorHandler.buildApiErrorMessage(error);

                    if (shouldLogError) {
                        new ApiError(this.errorMessage, true);
                    }
                })
                .finally(async () => {
                    await this.loader.dismiss();
                    this.loading = false;
                })
                .catch(() => {
                    //
                });
        }
    }
</script>
